<!--
  File name: Notifications.vue
  Program Description:
    - This component shows the list of notifications that are read and unread by the user
    - User can clear notifications in this component
-->
<template>
  <v-container class="content">
    <v-card class="card-style">
      <v-card-title>
        <v-row>
          <v-col
            cols="12"
            align="center"
          >
            <span class="title"> Notifications </span>
          </v-col>
        </v-row>
      </v-card-title>
      <v-container>
        <v-row style="padding: 0 15px;">
          <v-col
            cols="4"
            align="left"
          >
            <span
              class="clear-header"
              style="cursor: pointer"
              @click="readAllNotifications"
            >
              Mark all as Read
            </span>
          </v-col>
          <v-spacer />
          <v-col
            cols="4"
            align="right"
          >
            <span
              class="clear-header"
              style="cursor: pointer"
              @click="clearNotifications(true)"
            > Clear notifications </span>
          </v-col>
        </v-row>
      </v-container>
      <v-card-text
        style="padding: 0 15px;"
      >
        <v-container
          scrollable
          align="center"
        >
          <div v-if="!notifications.length">
            <v-row>
              <v-col
                cols="12"
                align="center"
              >
                <span style="font-size: 32px !important">You have no new notifications yet.
                </span>
              </v-col>
            </v-row>
          </div>
          <div v-else>
            <v-container v-if="recentNotifs.length">
              <v-row class="notif-header">
                Recent
              </v-row>
              <v-row
                v-for="(notif, index) in recentNotifs"
                :key="`recentNotif.${index}`"
                class="notification-panels"
                :style="notificationBackground(notif)"
                @click="navigateToNotification(notif)"
              >
                <v-col
                  cols="12"
                  align="center"
                >
                  <v-row>
                    <v-col
                      cols="1"
                      align="right"
                      justify="right"
                    >
                      <v-icon
                        v-if="!notif.seen"
                        color="#0F8C15"
                        size="20"
                        class="details"
                        style="align-items: right;"
                      >
                        mdi-checkbox-blank-circle
                      </v-icon>
                    </v-col>
                    <v-col
                      cols="2"
                      justify="left"
                      align="left"
                    >
                      <v-img
                        :src="`${$metaData.app.userIcon}`"
                        style="width: 64px;"
                      />
                    </v-col>
                    <v-col
                      cols="9"
                      justify="left"
                      align="left"
                    >
                      <v-row>
                        <span style="font-weight: 400px !important; font-size: 20px !important;"> {{ notif.message }} </span>
                      </v-row>
                      <v-row>
                        <span style="font-weight: 400px !important; font-size: 12px !important;"> {{ getDate(notif.dateSent) }} </span>
                      </v-row>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
            </v-container>
            <v-container
              v-if="oldNotifs.length"
            >
              <v-row
                class="notif-header"
              >
                Earlier
              </v-row>
              <v-spacer />
              <v-row
                v-for="(notif, index) in oldNotifs"
                :key="`oldNotif.${index}`"
                :style="notificationBackground(notif)"
                class="notification-panels"
                @click="navigateToNotification(notif)"
              >
                <v-col
                  cols="12"
                >
                  <v-row>
                    <v-col
                      cols="1"
                      align="right"
                      justify="right"
                    >
                      <v-icon
                        v-if="!notif.seen"
                        color="#0F8C15"
                        size="20"
                        class="details"
                        style="align-items: right;"
                      >
                        mdi-checkbox-blank-circle
                      </v-icon>
                    </v-col>
                    <v-col
                      cols="1"
                      justify="left"
                      align="left"
                    >
                      <v-img
                        :src="`${$metaData.app.userIcon}`"
                        style="width: 64px;"
                      />
                    </v-col>
                    <v-col
                      cols="10"
                      justify="center"
                      align="center"
                      class="notif-details"
                    >
                      <v-row>
                        <span class="messages"> {{ notif.message }} </span>
                      </v-row>
                      <v-row>
                        <span class="dates"> {{ getDate(notif.dateSent) }} </span>
                      </v-row>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
            </v-container>
          </div>
        </v-container>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import { socket } from '@/socket'

export default {
  name: 'Notifications',
  data: () => ({
    users: [],
    notifications: [],
    recentNotifs: [],
    oldNotifs: [],
    hasSorted: false
  }),
  watch: {
    '$store.state.notifications.notifications': {
      async handler (value) {
        this.hasSorted = false
        this.recentNotifs = []
        this.oldNotifs = []
        this.notifications = value
        await this.sortNotifications()
      },
      immediate: true
    }
  },
  async beforeCreate () {
    socket.on('connect', () => {
      this.users.forEach((user) => {
        if (user.self) {
          user.connected = true
        }
      })
    })
    socket.on('disconnect', () => {
      this.users.forEach((user) => {
        if (user.self) {
          user.connected = false
        }
      })
    })
  },
  async created () {
    if (!this.notifications.length) {
      this.fetchNotifications()
    }
  },
  async beforeDestroy () {
    this.hasSorted = false
    await this.clearNotifications()
  },
  methods: {
    // set background color of notification depending if its seen or not
    notificationBackground (notification) {
      return !notification.seen ? 'background-color: #E5FFE7;' : '' // #E5FFE7
    },
    // Mark all notification as read
    readAllNotifications () {
      this.$store.dispatch('showDialog', {
        title: 'Mark All as Read',
        message: 'Are you sure you want to mark all notifications as read?',
        buttonLabel: 'Yes',
        buttonColor: 'primary',
        callback: () => {
          socket.emit('read all notifications')
          this.notifications.forEach(notif => (notif.seen = true))
          this.notificationsCount()
        }
      })
    },
    // clear current notifications
    async clearNotifications (clearState = false) {
      if (clearState) {
        socket.emit('clear notifications')
        await this.$store.dispatch('notifications/reset')
      }
      this.notifications = []
      this.recentNotifs = []
      this.oldNotifs = []
    },
    // adjusting format of dateSent prop
    getDate (dateSent) {
      const date = new Date(dateSent)
      const dateNow = new Date()
      if (date.getDate() === dateNow.getDate() && date.getMonth() === dateNow.getMonth() && date.getFullYear() === dateNow.getFullYear()) {
        return date.toLocaleTimeString('en-US')
      }
      const options = { day: 'numeric', year: 'numeric', month: 'long' }
      return date.toLocaleDateString('en-US', options).concat(', ', date.toLocaleTimeString('en-US'))
    },
    // sort notifications based on time
    async sortNotifications () {
      const dateNow = new Date()

      !this.hasSorted && this.notifications.forEach((item) => {
        const date = new Date(item.dateSent)
        if (date.getDate() === dateNow.getDate() && date.getMonth() === dateNow.getMonth() && date.getFullYear() === dateNow.getFullYear()) {
          this.recentNotifs.unshift(item)
        } else {
          this.oldNotifs.unshift(item)
        }
      })
      this.hasSorted = true
    },
    // reroute to selected notification
    async navigateToNotification (value) {
      socket.emit('update notifications', value._id)
      this.notifications.map((notif) => {
        if (notif._id === value._id) {
          notif.seen = true
        }
      })
      await this.notificationsCount()
      const params = {
        operation: 'view',
        id: value.collectionId
      }
      if (value.collectionName === 'DataBreachView') {
        params.dataTab = 'pdbnf'
      }

      await this.$router.push({
        name: value.collectionName,
        params
      })
      this.$emit('input', false)
    },
    // update unseen notifications count
    async notificationsCount () {
      await this.$store.dispatch('notifications/setNotifications', { notifications: this.notifications })
    },
    // fetch notifications from the BE
    fetchNotifications () {
      socket.emit('fetch notifications')
    }
  }
}
</script>

<style scoped>
  .content {
    width: 70%;
    padding: 125px 100px;
  }
  .card-style {
    padding: 50px;
  }
  .details {
    margin: 0;
    top: 50%;
    -ms-transform: translateY(-50%);
    transform: translateY(-50%);
  }
  .title {
    font-family: Source Sans Pro, sans-serif !important;
    font-weight: 800 !important;
    font-size: 34px !important;
  }
  .notification-panels {
    height: 100px;
    border-style: solid;
    border-width: 0 0 2px 0;
    border-color: #1D9F23;
    margin: 0 0 10pt 0;
    border-radius: 4pt;
  }
  .notif-header {
    font-size: 20pt;
    font-weight: 900 !important;
    padding: 0 0 20px 10px;
  }
  .notif-details {
    padding: 30px 0 0 30px !important;
  }
  .clear-header {
    font-size: 14pt;
    font-weight: 700 !important;
  }
  .clear-header:hover {
    background-color: rgb(225, 225, 225);
    transition: 0.5s
  }
  .messages {
    font-size: 20px;
    font-weight: 700 !important;
  }
  .dates {
    font-size: 14px;
    font-weight: 300 !important;
  }
</style>
